import { computed, reactive, toRefs } from "@vue/composition-api";
import MultiSelectChatInterface from "@/interfaces/multiSelectChat.interface";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import ChatsListChatInterface from "@/interfaces/inbox/chatsListChat.interface";
// @ts-ignore
import i18n from "@/libs/i18n";

interface RootState {
    enabled: boolean;
    multiSelectChats: MultiSelectChatInterface[];
    loading: boolean;
    error: boolean;
}

const state: RootState = reactive({
    enabled: false,
    multiSelectChats: [],
    loading: false,
    error: false,
});

const limit = 10;

export default function useMultiSelect(toast?: any) {
    const fetchMultiSelectChats = (): void => {
        state.loading = true;
        state.multiSelectChats = JSON.parse(localStorage.getItem("multiSelectChats") as string) ?? [];
        state.loading = false;
    };

    // Filter the multi select chats to remove chats from the multi select which are not included in the parameter.
    // Initially intended to keep the localStorage in sync with the chats list
    const filterMultiSelectChats = (chats: ChatsListChatInterface[]) => {
        const chatIds = chats.map((chat) => chat.id);

        state.multiSelectChats = state.multiSelectChats.filter((chat) => chatIds.includes(chat.id));

        _setLocalStorage();
    };

    const clearMultiSelect = (chats?: MultiSelectChatInterface[]): void => {
        if (chats) {
            const chatIds = chats.map((chat) => chat.id);
            state.multiSelectChats = state.multiSelectChats.filter((chat) => !chatIds.includes(chat.id));
        } else {
            state.multiSelectChats = [];
        }

        _setLocalStorage();
    };

    const multiSelectEnabled = computed({
        get: () => {
            const storedMultiSelectEnabled = Boolean(JSON.parse(localStorage.getItem("multiSelectEnabled") as string));
            if (typeof storedMultiSelectEnabled !== "undefined") {
                state.enabled = storedMultiSelectEnabled;
            }

            if (state.enabled) {
                fetchMultiSelectChats();
            }

            return state.enabled;
        },
        set: (value: boolean) => {
            if (value) {
                fetchMultiSelectChats();
                localStorage.setItem("multiSelectEnabled", "1"); // String "boolean", consistent with other localStorage user settings...
            } else {
                localStorage.removeItem("multiSelectEnabled");
                localStorage.removeItem("multiSelectChats");
                state.multiSelectChats = [];
            }

            return (state.enabled = value);
        },
    });

    const setMultiSelectChats = (chat: MultiSelectChatInterface): void => {
        const multiSelectChatsIndex = state.multiSelectChats.findIndex((selectedChat) => selectedChat.id === chat.id);

        if (multiSelectChatsIndex === -1) {
            if (state.multiSelectChats.length >= limit) {
                if (toast) {
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("multi-select"),
                            text: i18n.t("multi-select-limit", { limit }),
                            icon: "times",
                            variant: "error",
                        },
                    });
                }

                return;
            }

            state.multiSelectChats.push(chat);
        } else {
            state.multiSelectChats.splice(multiSelectChatsIndex, 1);
        }

        _setLocalStorage();
    };

    const _setLocalStorage = () => {
        if (state.multiSelectChats.length > 0) {
            localStorage.setItem("multiSelectChats", JSON.stringify(state.multiSelectChats));
        } else {
            localStorage.removeItem("multiSelectChats");
        }
    };

    return {
        multiSelectEnabled,
        clearMultiSelect,
        fetchMultiSelectChats,
        setMultiSelectChats,
        filterMultiSelectChats,
        ...toRefs(state),
    };
}
